import { useCallback, useState } from "react";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "../../../hooks/useAPI";

const EXECUTE_ERROR_MESSAGE =
  "Something went wrong. Please try again or contact support.";

interface UseCommandLinkExecuteProps<TParams> {
  commandName: string;
  token: string;
  params: TParams;
}

interface UseCommandLinkExecuteResult<TResponse> {
  execute: (onSuccess?: (data: TResponse) => void) => void;
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Unauthenticated POST to /command-links/execute. The signed token in the
 * request body is the sole authorization; we deliberately bypass useAPI's
 * authenticated path because that path calls signout() when no idToken is
 * present, which would redirect an email-unsubscribe recipient (who is by
 * definition not logged in) to the login page mid-click.
 */
export function useCommandLinkExecute<TResponse, TParams>(
  props: UseCommandLinkExecuteProps<TParams>,
): UseCommandLinkExecuteResult<TResponse> {
  const { commandName, token, params } = props;
  const { unauthenticatedApi } = useAPI();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const execute = useCallback(
    (onSuccess?: (data: TResponse) => void) => {
      setLoading(true);
      setError(null);
      unauthenticatedApi<TResponse>("post", "/command-links/execute", {
        command_name: commandName,
        token,
        params,
      })
        .then((response) => {
          setSuccess(true);
          if (onSuccess) onSuccess(response.data);
        })
        .catch((e) => {
          setError(extractAPIRequestErrorMessage(e, EXECUTE_ERROR_MESSAGE));
          setSuccess(false);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [commandName, token, params, unauthenticatedApi],
  );

  return {
    execute,
    loading,
    error,
    success,
  };
}
